// Copyright (c) 2021, 37.78 Tecnologia Ltda. All rights reserved.

import React, { lazy } from "react";

import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useSearchParams,
} from "react-router-dom";

import { ApplicationLayout } from "../components";
import { useAuth, userStates } from "../contexts/auth";
import { AuthenticatedGuards, UnauthenticatedGuards } from "./authGuards";
import { DailyRoom } from "./RedirectRoutes";
import { AuthenticatedUser } from "./user";
import FullPageLoading from "./user/components/FullPageLoading";

const Appointment = lazy(() => import("./appointment"));
const Appointments = lazy(() => import("./appointments"));
const HealthStatus = lazy(() => import("./health-status"));
const Home = lazy(() => import("./home"));
const RouteNotFound = lazy(() => import("./404"));
const Services = lazy(() => import("./services"));
const ServicesRoutes = lazy(() => import("./services/ServicesRoutes"));

const Login = lazy(() => import("./user/login/loginFlow"));
const TokenLogin = lazy(() => import("./user/login/tokenLoginFlow"));
const ExternalHome = lazy(() => import("./external-home/ExternalHome"));
const ResetPassword = lazy(() => import("./user/resetPassword"));
const SignUp = lazy(() => import("./user/sign-up"));
const VerifyEmail = lazy(() => import("./user/verifyEmail"));

export const ApplicationRoutes = () => {
  const {
    userState: { logged },
  } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode");

  if (logged === userStates.UNKNOWN) {
    return <FullPageLoading />;
  }

  return (
    <Routes>
      <Route path="redirect-dailyco/:id" element={<DailyRoom />} />
      <Route path="user/login/:token" element={<TokenLogin />} />
      <Route
        path="user"
        element={
          (mode === "resetPassword" && <ResetPassword />) ||
          (mode === "verifyEmail" && <VerifyEmail />) || (
            <Navigate to="user/login" />
          )
        }
      />

      <Route element={<UnauthenticatedGuards />}>
        <Route path="/" element={<ExternalHome />} />
        <Route path="user">
          <Route path="signup" element={<SignUp />} />
          <Route path="login" element={<Login />} />
          <Route path="login/password" element={<Login step={1} />} />
        </Route>
        <Route
          path="*"
          element={<Navigate to="user/login" state={{ from: location }} />}
        />
      </Route>

      <Route element={<AuthenticatedGuards />}>
        <Route path="/" element={<Navigate to="home" />} />
        <Route path="appointment/*" element={<Appointment />} />
        <Route path="user/*" element={<AuthenticatedUser />} />
        <Route path="services/*" element={<ServicesRoutes />} />
        <Route element={<ApplicationLayout />}>
          <Route path="home" element={<Home />} />
          <Route path="appointments" element={<Appointments />} />
          <Route path="services" element={<Services />} />
          <Route path="health-status/*" element={<HealthStatus />} />
        </Route>
      </Route>
      <Route path="*" element={<RouteNotFound />} />
    </Routes>
  );
};
